import moment from 'moment';
import React from 'react';
import { omit } from 'lodash';
import { STATEMENT } from '../../constants';
import DriModuleForm from '../../components/DriModuleForm';
import DriOperations from '../../actions/driOperations';
import { can } from '../../utils/auth';

const DriStatementGeneration = () => {
  const formatStatementEndDate = dateTime => {
    return moment.utc(dateTime).endOf('month').format('YYYY-MM-DD');
  };

  const processStatementRegenerationApiRequest = async (
    params,
    { setSuccessMessage, setErrorMessage, setSevereFailureErrorMessage },
  ) => {
    try {
      const { allContracts, contractIds, setStatementAutoApproval } = params;
      if (allContracts === false && (contractIds || []).length === 0)
        throw new Error('Contract ID required');

      const formattedStatementEndDate = params.statementEndDate
        ? formatStatementEndDate(params.statementEndDate)
        : null;
      const formattedTransactionPeriodCutOff = params.transactionPeriodCutOff
        ? formatStatementEndDate(params.transactionPeriodCutOff)
        : null;
      let confirmMessage = `${'Ready to regenerate Statements for the following:\n\nContract ID:\n'}${
        allContracts ? 'ALL' : contractIds.join('\n')
      }\n\n`;

      if (formattedStatementEndDate) {
        confirmMessage += `Statement Period:\n${moment(formattedStatementEndDate).format(
          'MMMM YYYY',
        )}\n\n`;
      }

      confirmMessage += 'Would you like to proceed?';

      // Confirmation alerts are fine here since this code is meant to be used by DRIs only.
      // eslint-disable-next-line no-alert
      if (!window.confirm(confirmMessage)) return;

      // Make an array of the keys that have a true value
      const status = Object.keys(params.status).filter(key => params.status[key]);

      let requestParams = {
        ...params,
        status,
      };

      if (formattedStatementEndDate) {
        requestParams.statementEndDate = formattedStatementEndDate;
      }
      if (formattedTransactionPeriodCutOff) {
        requestParams.transactionPeriodCutOff = formattedTransactionPeriodCutOff;
      }
      if (allContracts) {
        requestParams = omit(requestParams, 'contractIds');
      } else {
        requestParams = omit(requestParams, 'allContracts');
      }
      if (setStatementAutoApproval !== true) {
        requestParams = omit(requestParams, 'setStatementAutoApproval');
      }
      const response = await DriOperations.regenerateMultipleStatements(requestParams);

      if (response.ok) {
        const responseJson = await response.json();
        const { failedStatements = [], successfulContractIds = [] } = responseJson;

        if (successfulContractIds.length > 0) {
          setSuccessMessage(
            `Initiated statement regeneration for the following contract Ids:\n${successfulContractIds.join(
              '\n',
            )}\n\n*Actual statement regeneration will occur within the next several minutes via Calc-services function "statements"`,
          );
        }

        if (failedStatements.length > 0) {
          const failedMessages = failedStatements.map(
            statementErrorLog =>
              `Id: ${statementErrorLog.contractId}\nError: ${statementErrorLog.errorMessage}`,
          );
          setErrorMessage(
            `Failed to regenerate statements for the following contract Ids:\n\n${failedMessages.join(
              '\n\n',
            )}`,
          );
        }

        if (successfulContractIds.length === 0 && failedStatements.length === 0) {
          setSuccessMessage('No contracts with GENERATED status to regenerate statements for.');
        }
      } else if (response.error) {
        throw new Error(JSON.stringify(response.error));
      } else {
        throw new Error(`${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setSevereFailureErrorMessage(`${error}`);
    }
  };

  const generateForm = (subModuleHeader, formId, isPublicPreview) => (
    <DriModuleForm
      subModuleHeader={subModuleHeader}
      buttonText={isPublicPreview ? 'Public Preview Available' : 'Generate Statements'}
      formId={formId}
      isPublicPreview={isPublicPreview}
      onSubmit={async (params, callbacks) =>
        processStatementRegenerationApiRequest(params, callbacks)
      }
      inputs={[
        {
          id: 'contractIds',
          label: 'Contract Ids',
          type: 'picker',
          disabledIf: 'allContracts',
          config: { pickerSuggestionsProps: { noResultsFoundText: 'Duplicate ContractId' } },
        },
        ...(!isPublicPreview
          ? [
              {
                id: 'allContracts',
                label: 'All Contracts',
                type: 'checkbox',
              },
              {
                id: 'statementEndDate',
                label: 'End Date',
                type: 'date',
                disabledIf: window.config.disabledInProd ? 'disabledInProd' : null,
                selectedDate: window.config.disabledInProd
                  ? null
                  : moment.utc().subtract(1, 'months').toDate(),
                config: {
                  highlightSelectedMonth: true,
                  maxDate: moment.utc().subtract(1, 'months').toDate(),
                },
              },
              {
                id: 'status',
                label: 'Status (if statement exists):\n',
                type: 'dropdown',
                config: {
                  multiSelect: true,
                  options: [
                    { key: STATEMENT.STATUS.PENDING, text: STATEMENT.STATUS.PENDING },
                    { key: STATEMENT.STATUS.INFLIGHT, text: STATEMENT.STATUS.INFLIGHT },
                    { key: STATEMENT.STATUS.GENERATED, text: STATEMENT.STATUS.GENERATED },
                    { key: STATEMENT.STATUS.REJECTED, text: STATEMENT.STATUS.REJECTED },
                    {
                      key: STATEMENT.STATUS.APPROVED,
                      text: STATEMENT.STATUS.APPROVED,
                      disabled: true,
                    },
                    {
                      key: STATEMENT.STATUS.INVOICEGENERATED,
                      text: STATEMENT.STATUS.INVOICEGENERATED,
                      disabled: true,
                    },
                    {
                      key: STATEMENT.STATUS.INVOICEGENERATIONSKIPPED,
                      text: STATEMENT.STATUS.INVOICEGENERATIONSKIPPED,
                      disabled: true,
                    },
                  ],
                  defaultSelectedKeys: [
                    STATEMENT.STATUS.PENDING,
                    STATEMENT.STATUS.INFLIGHT,
                    STATEMENT.STATUS.GENERATED,
                  ],
                },
              },
              can('manageDRIOps')
                ? {
                    id: 'setStatementAutoApproval',
                    label: 'Auto Approval',
                    type: 'toggle',
                  }
                : null,
            ]
          : [
              {
                id: 'transactionPeriodCutoff',
                label: 'Transaction Period Cutoff',
                type: 'date',
                disabledIf: window.config.disabledInProd ? 'disabledInProd' : null,
                selectedDate: window.config.disabledInProd
                  ? null
                  : moment.utc().subtract(1, 'months').toDate(),
                config: {
                  highlightSelectedMonth: true,
                  maxDate: moment.utc().subtract(1, 'months').toDate(),
                },
              },
            ]),
      ]}
    />
  );

  return (
    <>
      {generateForm(
        'Generate Statements (includes generate and regenerate)',
        'statement-regeneration',
        false,
      )}
      {generateForm(
        'Generate Preview Statements (includes generate and regenerate)',
        'statement-preview',
        true,
      )}
    </>
  );
};

export default DriStatementGeneration;
